import Vapi from "@vapi-ai/web";

const states = [
  "loading", "welcome", "connecting", "active", "processing",
  "completed", "unavailable", "failed"
];
const pathSegments = location.pathname.split("/").filter(Boolean);
const interviewSegment = pathSegments.findIndex(
  segment => segment.toLowerCase() === "interview");
const token = decodeURIComponent(
  interviewSegment >= 0 ? pathSegments[interviewSegment + 1] || "" : "");
let interview;
let vapi;
let session;
let callIdSent = false;
let pollTimer;
let microphoneReady = false;
let callConnected = false;
let connectionFailureShown = false;
let connectionTimer;
let connectionGeneration = 0;
let failureReport = Promise.resolve();
let playbackContext;
let interviewerAudioSource;
let interviewerAudioTrack;
let audioReady = false;

const byId = id => document.getElementById(id);
const show = name => states.forEach(id => byId(id).classList.toggle("hidden", id !== name));
const text = (id, value) => { byId(id).textContent = value || ""; };

async function api(path, options = {}) {
  const response = await fetch(`/api/interview/${encodeURIComponent(token)}${path}`, {
    cache: "no-store",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options
  });
  if (response.status === 204) return null;
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "The request could not be completed.");
  return body;
}

function unavailable(title, message) {
  text("unavailable-title", title);
  text("unavailable-message", message);
  show("unavailable");
}

function renderStatus(status) {
  if (status === "Completed") {
    clearInterval(pollTimer);
    show("completed");
  } else if (status === "Expired") {
    clearInterval(pollTimer);
    unavailable("Interview link expired", "Please contact the recruitment team if you need a new invitation.");
  } else if (status === "Cancelled") {
    clearInterval(pollTimer);
    unavailable("Invitation cancelled", "This screening invitation is no longer active.");
  } else if (status === "Failed") {
    clearInterval(pollTimer);
    show("failed");
  } else if (status === "Processing") {
    show("processing");
  }
}

async function initialize() {
  if (!/^[A-Za-z0-9_-]{32,256}$/.test(token)) {
    unavailable("Interview unavailable", "This secure interview link is invalid.");
    return;
  }
  try {
    interview = await api("");
  } catch {
    unavailable("Interview unavailable", "This secure interview link is invalid, expired, or no longer active.");
    return;
  }

  renderStatus(interview.status);
  if (["Completed", "Expired", "Cancelled"].includes(interview.status)) return;
  if (interview.status === "Processing") {
    beginStatusPolling();
    return;
  }
  if (["Connecting", "InProgress"].includes(interview.status)) {
    unavailable("Interview already active", "This interview is already connected in another browser tab.");
    return;
  }
  text("candidate-name", interview.candidateFirstName);
  text("position-name", interview.positionName);
  text("active-position", interview.positionName);
  text("company-name", interview.companyName);
  text("duration", `About ${interview.estimatedDurationMinutes || 15} minutes (flexible)`);
  text("expires", new Date(interview.expiresOn).toLocaleString());
  text("consent-text", interview.consentText);
  if (interview.recordingEnabled) {
    byId("recording-consent-row").classList.remove("hidden");
    text("recording-consent-text", interview.recordingConsentText);
  }
  if (interview.status === "Failed") {
    text(
      "failure-message",
      interview.lastError || "The previous attempt did not complete. Check your connection and try again.");
  }
  syncStartButton();
  show(interview.status === "Failed" ? "failed" : "welcome");
  await initializeMicrophoneState();

  // Opening telemetry must never prevent a valid interview from loading.
  void api("/client-ready", { method: "POST", body: "{}" }).catch(() => {});
}

function syncStartButton() {
  byId("start").disabled =
    !interview?.canStart ||
    !microphoneReady ||
    !byId("consent").checked ||
    (interview?.recordingEnabled && !byId("recording-consent").checked);
}

function microphoneHelp() {
  const agent = navigator.userAgent;
  if (/Firefox/i.test(agent)) {
    return "Select the microphone icon in the address bar, allow access, then try again.";
  }
  if (/Safari/i.test(agent) && !/Chrome|Chromium|Edg/i.test(agent)) {
    return "In Safari, open Website Settings for this site, set Microphone to Allow, then try again.";
  }
  return "Select the lock or site controls icon beside the address bar, set Microphone to Allow, then try again.";
}

function setMicrophoneState(state, status, help = "") {
  const check = byId("microphone-check");
  const button = byId("enable-microphone");
  microphoneReady = state === "ready";
  check.classList.toggle("ready", state === "ready");
  check.classList.toggle("error-state", state === "error");
  text("microphone-icon", state === "ready" ? "✓" : "🎙");
  text("microphone-status", status);
  text("microphone-help", help);
  byId("microphone-help").classList.toggle("hidden", !help);
  button.disabled = state === "checking";
  button.classList.toggle("hidden", state === "ready");
  button.textContent = state === "checking" ? "Checking…" :
    state === "error" ? "Try Again" : "Enable Microphone";
  syncStartButton();
}

function microphoneError(error) {
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
    setMicrophoneState(
      "error",
      "Microphone access is blocked.",
      microphoneHelp());
  } else if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
    setMicrophoneState(
      "error",
      "No microphone was found.",
      "Connect or enable a microphone on your device, then try again.");
  } else if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
    setMicrophoneState(
      "error",
      "Your microphone is being used by another application.",
      "Close other meeting or recording applications, then try again.");
  } else {
    setMicrophoneState(
      "error",
      "The microphone could not be checked.",
      "Make sure this page is using HTTPS and your browser allows microphone access, then try again.");
  }
}

async function requestMicrophoneAccess() {
  if (!navigator.mediaDevices?.getUserMedia) {
    microphoneError(new DOMException("", "SecurityError"));
    return false;
  }
  setMicrophoneState("checking", "Waiting for microphone permission…");
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    setMicrophoneState("ready", "Microphone is enabled and ready.");
    return true;
  } catch (error) {
    microphoneError(error);
    return false;
  }
}

async function initializeMicrophoneState() {
  if (!navigator.mediaDevices?.getUserMedia) {
    microphoneError(new DOMException("", "SecurityError"));
    return;
  }
  try {
    const permission = await navigator.permissions?.query({ name: "microphone" });
    permission.onchange = () => {
      if (permission.state === "granted") {
        void requestMicrophoneAccess();
      } else if (permission.state === "denied") {
        microphoneError(new DOMException("", "NotAllowedError"));
      } else {
        setMicrophoneState(
          "required",
          "Enable your microphone before starting the interview.");
      }
    };
    if (permission.state === "granted") {
      await requestMicrophoneAccess();
    } else if (permission.state === "denied") {
      microphoneError(new DOMException("", "NotAllowedError"));
    }
  } catch {
    // Some browsers do not expose microphone state through the Permissions API.
  }
}

async function postClientEvent(eventType, providerCallId, message) {
  try {
    await api("/client-event", {
      method: "POST",
      body: JSON.stringify({
        eventType,
        providerCallId: providerCallId || null,
        attemptNumber: session?.attemptNumber || null,
        message: message || null
      })
    });
  } catch {
    // The authenticated provider webhook remains authoritative.
  }
}

function discoverCallId(message) {
  const id = message?.call?.id || message?.callId || message?.id;
  if (!callIdSent && typeof id === "string" && id.length > 0) {
    callIdSent = true;
    void postClientEvent("call-id", id);
  }
}

function errorText(value, depth = 0) {
  if (depth > 5 || value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map(item => errorText(item, depth + 1)).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    for (const key of ["message", "errorMsg", "error", "detail", "reason", "title"]) {
      const result = errorText(value[key], depth + 1);
      if (result) return result;
    }
  }
  return "";
}

function vapiErrorMessage(error) {
  return errorText(error) || "The voice interview connection failed. Please try again.";
}

function showConnectionFailure(error) {
  if (connectionFailureShown) return;
  clearTimeout(connectionTimer);
  connectionFailureShown = true;
  connectionGeneration++;
  const message = vapiErrorMessage(error);
  failureReport = postClientEvent("connection-failed", null, message);
  text("failure-message", message);
  show("failed");
}

function unlockAudioPlayback() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;
  playbackContext ??= new AudioContextClass();
  if (playbackContext.state === "suspended") {
    void playbackContext.resume();
  }
  const oscillator = playbackContext.createOscillator();
  const gain = playbackContext.createGain();
  gain.gain.value = 0.0001;
  oscillator.connect(gain);
  gain.connect(playbackContext.destination);
  oscillator.start();
  oscillator.stop(playbackContext.currentTime + 0.05);
}

function setAudioReady() {
  audioReady = true;
  text("audio-status", "Interviewer audio is connected.");
  byId("enable-audio").classList.add("hidden");
}

function showAudioRecovery() {
  audioReady = false;
  text(
    "audio-status",
    "Your browser blocked interviewer audio. Select the button below to enable it.");
  byId("enable-audio").classList.remove("hidden");
}

function stopInterviewerPlayback() {
  interviewerAudioSource?.disconnect();
  interviewerAudioSource = undefined;
  interviewerAudioTrack = undefined;
  audioReady = false;
}

function remoteInterviewerTrack() {
  const participants = vapi?.getDailyCallObject()?.participants?.() || {};
  const speaker = Object.values(participants).find(
    participant => !participant?.local && participant?.user_name === "Vapi Speaker");
  return speaker?.tracks?.audio?.persistentTrack ||
    speaker?.tracks?.audio?.track ||
    speaker?.audioTrack ||
    null;
}

async function playInterviewerTrack(track) {
  if (!track || track.readyState === "ended") return false;
  unlockAudioPlayback();
  if (!playbackContext) return false;
  if (playbackContext.state === "suspended") {
    await playbackContext.resume();
  }
  if (interviewerAudioTrack !== track) {
    interviewerAudioSource?.disconnect();
    interviewerAudioTrack = track;
    interviewerAudioSource = playbackContext.createMediaStreamSource(
      new MediaStream([track]));
    interviewerAudioSource.connect(playbackContext.destination);
    track.addEventListener("ended", () => {
      if (interviewerAudioTrack === track) showAudioRecovery();
    }, { once: true });
  }
  setAudioReady();
  return true;
}

async function selectSpeakerOutput() {
  if (!navigator.mediaDevices?.selectAudioOutput) return;
  const device = await navigator.mediaDevices.selectAudioOutput();
  if (!device?.deviceId) return;
  vapi?.setOutputDeviceAsync({ outputDeviceId: device.deviceId });
  if (playbackContext?.setSinkId) {
    await playbackContext.setSinkId(device.deviceId);
  }
}

async function enableInterviewerAudio() {
  unlockAudioPlayback();
  try {
    await selectSpeakerOutput();
  } catch {
    // Continue with the browser's default speaker if selection is cancelled.
  }
  try {
    vapi?.setVolume(1);
    const sdkPlayer = vapi?.getAudioPlayer();
    const track = remoteInterviewerTrack() ||
      sdkPlayer?.srcObject?.getAudioTracks?.()[0];
    if (!track) {
      text("audio-status", "Interviewer audio is still connecting. Please try again in a moment.");
      return;
    }
    if (sdkPlayer) sdkPlayer.muted = true;
    await playInterviewerTrack(track);
  } catch {
    showAudioRecovery();
  }
}

function attachVapiEvents(instance, generation) {
  const current = () => generation === connectionGeneration;
  instance.on("call-start", () => {
    if (!current()) return;
    clearTimeout(connectionTimer);
    callConnected = true;
    connectionFailureShown = false;
    show("active");
    text("call-status", "The interviewer is listening.");
    const track = remoteInterviewerTrack();
    if (track) void playInterviewerTrack(track).catch(showAudioRecovery);
  });
  instance.on("audio", player => {
    if (!current()) return;
    const track = player.srcObject?.getAudioTracks?.()[0];
    player.muted = true;
    if (track) {
      void playInterviewerTrack(track).catch(showAudioRecovery);
    } else {
      showAudioRecovery();
    }
  });
  instance.on("daily-participant-updated", participant => {
    if (!current() || participant?.local ||
        participant?.user_name !== "Vapi Speaker") return;
    const track = participant?.tracks?.audio?.persistentTrack ||
      participant?.audioTrack;
    if (track) void playInterviewerTrack(track).catch(showAudioRecovery);
  });
  instance.on("speech-start", () => {
    if (!current()) return;
    text("call-status", "The interviewer is speaking.");
    if (!audioReady) showAudioRecovery();
  });
  instance.on("speech-end", () => {
    if (current()) text("call-status", "The interviewer is listening.");
  });
  instance.on("message", message => {
    if (!current()) return;
    discoverCallId(message);
    if (message?.type === "transcript" && message.transcriptType === "final") {
      const line = document.createElement("p");
      line.textContent = `${message.role === "assistant" ? "Interviewer" : "You"}: ${message.transcript}`;
      byId("transcript").appendChild(line);
    }
  });
  instance.on("call-end", () => {
    if (!current()) return;
    clearTimeout(connectionTimer);
    stopInterviewerPlayback();
    if (callConnected) {
      callConnected = false;
      show("processing");
      beginStatusPolling();
    } else {
      showConnectionFailure({
        message: "The interview connection ended before it was established. Please try again."
      });
    }
  });
  instance.on("call-start-failed", error => {
    if (current()) showConnectionFailure(error);
  });
  instance.on("error", error => {
    if (current() && error?.type === "audio-start-failed") {
      showAudioRecovery();
    }
    // Vapi emits this event for both fatal and non-fatal SDK errors.
    // call-start-failed, a null start result, and the timeout are authoritative.
  });
}

async function startInterview() {
  unlockAudioPlayback();
  const generation = ++connectionGeneration;
  text("welcome-error", "");
  connectionFailureShown = false;
  callConnected = false;
  stopInterviewerPlayback();
  text("audio-status", "Interviewer audio is connecting.");
  byId("enable-audio").classList.add("hidden");
  clearTimeout(connectionTimer);
  if (vapi) {
    const previous = vapi;
    vapi = undefined;
    previous.removeAllListeners?.();
    try { previous.stop(); } catch { /* Already stopped. */ }
  }
  byId("start").disabled = true;
  if (!await requestMicrophoneAccess()) {
    text("welcome-error", "Enable microphone access before starting the interview.");
    syncStartButton();
    return;
  }
  show("connecting");
  try {
    await api("/consent", {
      method: "POST",
      body: JSON.stringify({
        consentAccepted: byId("consent").checked,
        recordingConsentAccepted: byId("recording-consent").checked
      })
    });
    session = await api("/start", { method: "POST", body: "{}" });
    callIdSent = false;
    const instance = new Vapi(
      session.vapiPublicKey,
      undefined,
      {
        avoidEval: true,
        alwaysIncludeMicInPermissionPrompt: true
      });
    instance.setVolume(1);
    vapi = instance;
    attachVapiEvents(instance, generation);
    const overrides = {
      firstMessage: session.firstMessage,
      variableValues: {
        ...session.variableValues,
        screeningPrompt: session.systemPrompt
      },
      artifactPlan: {
        recordingEnabled: session.recordingEnabled
      }
    };
    if (session.evaluationSchema) {
      overrides.artifactPlan.structuredOutputs = [{
        type: "ai",
        name: "Interview Evaluation",
        description: "Structured screening answers, scores, summary, and recommendation for human HR review.",
        schema: session.evaluationSchema
      }];
    }
    if (session.modelConfiguration) overrides.model = session.modelConfiguration;
    if (session.voiceId) {
      overrides.voice = { provider: "vapi", version: 2, voiceId: session.voiceId };
    }
    connectionTimer = setTimeout(() => {
      if (generation === connectionGeneration && !callConnected) {
        showConnectionFailure({
          message: "The voice interview took too long to connect. Check your internet connection and try again."
        });
        instance.stop();
      }
    }, 20000);
    const call = await instance.start(session.assistantId, overrides);
    if (generation !== connectionGeneration) return;
    if (!call && !connectionFailureShown) {
      showConnectionFailure({
        message: "The voice interview could not establish a connection. Please try again."
      });
      return;
    }
    discoverCallId(call);
  } catch (error) {
    if (generation === connectionGeneration) showConnectionFailure(error);
  }
}

function beginStatusPolling() {
  clearInterval(pollTimer);
  const check = async () => {
    try {
      const result = await api("/status");
      renderStatus(result.status);
    } catch {
      // Keep the processing state; the next bounded poll can recover.
    }
  };
  void check();
  pollTimer = setInterval(check, 3000);
  setTimeout(() => clearInterval(pollTimer), 20 * 60 * 1000);
}

byId("consent").addEventListener("change", syncStartButton);
byId("recording-consent").addEventListener("change", syncStartButton);
byId("enable-microphone").addEventListener("click", requestMicrophoneAccess);
byId("enable-audio").addEventListener("click", enableInterviewerAudio);
byId("start").addEventListener("click", startInterview);
byId("retry").addEventListener("click", async () => {
  if (interview) {
    await failureReport;
    try {
      interview = await api("");
    } catch {
      location.reload();
      return;
    }
    if (interview.status === "Processing") {
      show("processing");
      beginStatusPolling();
      return;
    }
    if (["Connecting", "InProgress"].includes(interview.status)) {
      unavailable(
        "Interview already active",
        "This interview is already connected in another browser tab.");
      return;
    }
    show("welcome");
    syncStartButton();
  } else {
    location.reload();
  }
});
byId("end").addEventListener("click", () => {
  if (vapi) vapi.stop();
  show("processing");
  beginStatusPolling();
});
window.addEventListener("pagehide", () => {
  clearInterval(pollTimer);
  clearTimeout(connectionTimer);
  stopInterviewerPlayback();
  if (session && navigator.sendBeacon) {
    const body = new Blob([JSON.stringify({
      eventType: "connection-failed",
      providerCallId: null,
      attemptNumber: session.attemptNumber,
      message: callConnected
        ? "The candidate closed the browser during the interview."
        : "The candidate closed the browser before the connection completed."
    })], { type: "application/json" });
    navigator.sendBeacon(
      `/api/interview/${encodeURIComponent(token)}/client-event`,
      body);
  }
  if (vapi) {
    try { vapi.stop(); } catch { /* The page is already closing. */ }
  }
});

void initialize();
